<template>
  <div v-if="loading" class="loader">
    <v-progress-circular
      class="loader"
      color="primary"
      size="64"
      indeterminate
    />
  </div>

  <v-layout v-else wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex class="d-flex align-center justify-space-between" xs12 mt-3>
          <h1 class="headline">{{ investorInfo.user.name }}</h1>

          <div class="d-flex align-items-center">
            <v-btn
              v-if="$can('investor_report_payment')"
              class="mr-3"
              color="warning"
              elevation="0"
              @click="
                () => {
                  dialog.value = true;
                  dialog.topUp = false;
                }
              "
            >
              {{ $t('investor-report.pay') }}
            </v-btn>
            <v-btn
              v-if="$can('investor_report_deposit')"
              class="mr-3"
              color="green"
              elevation="0"
              dark
              @click="
                () => {
                  dialog.value = true;
                  dialog.topUp = true;
                }
              "
            >
              {{ $t('investor-report.top-up') }}
            </v-btn>
            <v-btn
              v-if="$can('investor_report_payment_view')"
              color="info"
              elevation="0"
              @click="historyDialog.value = true"
            >
              {{ $t('investor-report.history') }}
            </v-btn>
          </div>
        </v-flex>

        <div class="d-flex align-items-center mt-3">
          <v-alert class="mb-0" :color="accountColor" text outlined dense>
            <div class="d-flex align-items-center">
              <v-icon :color="accountColor">mdi-account-cash</v-icon>
              <b class="ml-1 mr-2">{{ $t('investor-report.account') }}:</b>
              <b>{{ balance }}</b>
            </div>
          </v-alert>
          <v-alert
            class="ml-2 mb-0"
            :color="payment ? 'warning' : 'grey'"
            text
            outlined
            dense
          >
            <b> {{ $t('investor-report.pay') }}: {{ payment }} </b>
          </v-alert>
          <v-alert
            class="ml-2 mb-0"
            :color="deposit ? 'success' : 'grey'"
            text
            outlined
            dense
          >
            <b> {{ $t('common.table.deposit') }}: {{ deposit }} </b>
          </v-alert>
        </div>
      </v-layout>

      <div class="investor-info__search">
        <v-text-field
          class="search-field"
          ref="search"
          v-model="searchString"
          :placeholder="$t('rentsList.search.placeholder')"
          autocomplete="off"
          solo
          hide-details
        />
        <v-btn
          type="button"
          class="search-btn warning"
          depressed
          large
          @click="filters.search = searchString"
        >
          {{ $t('common.btns.search') }}
        </v-btn>
      </div>

      <div class="investor-info__filters">
        <v-menu
          ref="dateMenu"
          v-model="dateMenu"
          :close-on-content-click="false"
          :return-value.sync="filters.dates"
          transition="scale-transition"
          offset-y
          min-width="auto"
          max-width="300"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filters.dates"
              prepend-icon="mdi-calendar"
              class="investor-info__filters-item"
              readonly
              v-bind="attrs"
              v-on="on"
              :append-icon="filters.dates.length ? 'mdi-close' : ''"
              :label="$t('common.fields.rentDate')"
              @click:append="filters.dates = []"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.dates"
            color="primary lighten-1"
            width="280"
            range
            :locale="$i18n.locale"
            :selected-items-text="filters.dates.toString()"
            @input="changeDate"
          ></v-date-picker>
        </v-menu>

        <v-select
          v-model="filters.status"
          :label="$t('common.fields.status')"
          class="investor-info__filters-item"
          item-text="name"
          item-value="id"
          clearable
          :items="carStatuses.filter(({ id }) => id !== '0')"
        />

        <v-text-field
          v-model="expenseSearch"
          class="investor-info__filters-item"
          :label="$t('workersList.expenceSearch')"
        >
          <template #append>
            <div class="d-flex mt-1">
              <v-btn
                v-if="expenseSearch"
                class="mr-1"
                icon
                x-small
                @click="
                  () => {
                    expenseSearch = '';
                    filters.expence_comment = '';
                  }
                "
              >
                <v-icon small> mdi-close </v-icon>
              </v-btn>
              <v-btn
                color="warning"
                elevation="0"
                rounded
                x-small
                @click="filters.expence_comment = expenseSearch"
              >
                <v-icon small> mdi-magnify </v-icon>
              </v-btn>
            </div>
          </template>
        </v-text-field>
      </div>
    </v-flex>

    <v-flex sm12>
      <v-data-table
        ref="investor-table"
        class="investor-table"
        :height="tableHeight"
        :headers="headers"
        :items="investorCars"
        :loading="carsLoading"
        :items-per-page="-1"
        hide-default-footer
      >
        <template #body="{ items }">
          <tbody v-if="items.length > 0">
            <tr v-for="(item, key) in items" :key="key">
              <td>{{ item.num }}</td>
              <td>{{ item.created_at || '-' }}</td>
              <td>{{ item.car.number || '-' }}</td>
              <td>{{ (item.driver && item.driver.user.name) || '-' }}</td>
              <td>{{ item.car_status_name || '-' }}</td>
              <td>
                {{ formatAmount(item.income_total) }}
                <v-tooltip v-if="item.income_comment" top color="warning">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      color="warning"
                      small
                      v-on="on"
                      v-bind="attrs"
                    >
                      mdi-comment-text-outline
                    </v-icon>
                  </template>
                  <div v-html="item.income_comment" />
                </v-tooltip>
              </td>
              <td v-if="$can('investor_report_explanation')">
                {{ item.explanation || '-' }}
              </td>
              <td>
                {{ formatAmount(item.expense_total) }}
                <v-tooltip v-if="item.expense_comment" top color="warning">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      color="warning"
                      small
                      v-on="on"
                      v-bind="attrs"
                    >
                      mdi-comment-text-outline
                    </v-icon>
                  </template>
                  <div v-html="item.expense_comment" />
                </v-tooltip>
              </td>
              <td>
                {{ formatAmount(item.summary) }}
              </td>
            </tr>
            <tr class="investor-table__totals grey lighten-3">
              <td>
                <b>{{ $t('common.total') }}</b>
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <b>{{ formatAmount(incomeTotal) }}</b>
              </td>
              <td v-if="$can('investor_report_explanation')"></td>
              <td>
                <b>{{ formatAmount(expenseTotal) }}</b>
              </td>
              <td>
                <b>{{ formatAmount(remainderTotal) }}</b>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td class="text-center" colspan="8">
                {{ $t('common.table.noData') }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-flex>

    <investor-report-dialog
      v-model="dialog.value"
      :item="investorInfo"
      :top-up="dialog.topUp"
      @updated="proccessResult"
    />

    <transactions-history-dialog
      v-model="historyDialog.value"
      :items="history"
      @updated="proccessResult"
    />

    <v-snackbar v-model="snack.value" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex';
import InvestorReportDialog from '@/components/investor-report/InvestorReportDialog.vue';
import TransactionsHistoryDialog from '@/components/investor-report/TransactionsHistoryDialog.vue';
import formatAmount from '@/helpers/formatAmount';
import calcTableHeight from '@/helpers/calcTableHeight';

export default {
  components: { InvestorReportDialog, TransactionsHistoryDialog },
  data: () => ({
    loading: false,
    carsLoading: false,
    dateMenu: false,
    tableHeight: 'auto',
    searchString: '',
    expenseSearch: '',
    filters: {
      search: '',
      dates: [],
      status: '',
      expence_comment: '',
    },
    dialog: {
      value: false,
      topUp: false,
    },
    historyDialog: {
      value: false,
      items: [],
    },
    snack: {
      value: false,
      color: null,
      message: null,
    },
  }),
  watch: {
    filters: {
      deep: true,
      handler(value) {
        if (value.dates.length === 1) return;
        this.fetchList(value);
      },
    },
    loading(val) {
      if (val) return;

      this.$nextTick(() => {
        const table = this.$refs['investor-table'];
        this.tableHeight = calcTableHeight(table.$el);
      });
    },
  },
  computed: {
    ...mapState({
      investorInfo: (state) => state['investor-report'].investorInfo,
      investorCars: (state) => state['investor-report'].investorCars,
      carStatuses: (state) => state['workers-board'].carStatuses,
      balance: (state) => state['investor-report'].investorInfoBalance,
      payment: (state) => state['investor-report'].investorInfoPayment,
      deposit: (state) => state['investor-report'].investorInfoDeposit,
      history: (state) => state['investor-report'].investorInfoHistory,
    }),
    headers() {
      const items = [
        { text: this.$t('common.number'), sortable: false },
        { text: this.$t('common.fields.bookingDate'), sortable: false },
        { text: this.$t('auto.fields.number'), sortable: false },
        { text: this.$t('sidebar.drivers'), sortable: false },
        { text: this.$t('common.fields.status'), sortable: false },
        { text: this.$t('common.table.coming'), sortable: false },
        {
          text: this.$t('workersList.explanation'),
          permission: 'investor_report_explanation',
          sortable: false,
        },
        { text: this.$t('common.table.consumption'), sortable: false },
        { text: this.$t('investorList.remainder'), sortable: false },
      ];

      return items.filter(({ permission }) => this.$can(permission));
    },
    accountColor() {
      const total = Number(this.balance);
      let color = 'success';

      if (total < 0) color = 'error';
      else if (total === 0) color = 'grey';

      return color;
    },
    incomeTotal() {
      /* eslint-disable */
      return this.investorCars.reduce((total, item) => {
        return (total += item.income_total);
      }, 0);
      /* eslint-enable */
    },
    expenseTotal() {
      /* eslint-disable */
      return this.investorCars.reduce((total, item) => {
        return (total += item.expense_total);
      }, 0);
      /* eslint-enable */
    },
    remainderTotal() {
      /* eslint-disable */
      return this.investorCars.reduce((total, item) => {
        return (total += item.summary);
      }, 0);
      /* eslint-enable */
    },
    today() {
      const date = new Date();
      const formatNum = (v) => (v < 10 ? `0${v}` : v);

      return `${date.getFullYear()}-${formatNum(
        date.getMonth() + 1,
      )}-${date.getDate()}`;
    },
  },
  methods: {
    formatAmount,
    changeDate(val) {
      if (val.length === 2) {
        this.$refs.dateMenu.save(this.filters.dates);
      }
    },
    async fetchList(params) {
      this.carsLoading = true;
      await this.$store.dispatch('investor-report/fetchInvestorCars', {
        id: this.$route.params.investorId,
        params,
      });
      this.carsLoading = false;
    },
    async fetchInvestor() {
      await this.$store.dispatch(
        'investor-report/fetchInvestorInfo',
        this.$route.params.investorId,
      );
    },
    async fetchPaymentTypes() {
      const types = this.$store.state['investor-report'].paymentTypes;

      if (types.length > 0) return;
      await this.$store.dispatch('investor-report/fetchPaymentTypes');
    },
    proccessResult({ success, error }) {
      if (success) {
        this.dialog.value = false;
        this.fetchList(this.filters);
        this.fetchInvestor();
      }

      const snack = {
        value: true,
        color: 'success',
        message: this.$t('common.saved'),
      };

      if (error) {
        snack.color = 'error';
        snack.message = this.$t('common.errors.APIerror');
      }

      this.snack = snack;
    },
  },
  async created() {
    this.loading = true;

    await Promise.all([
      this.fetchInvestor(),
      this.fetchPaymentTypes(),
      this.$store.dispatch('workers-board/fetchDialogProps'),
    ]);

    this.filters.dates = [this.today, this.today];
    this.loading = false;
  },
};
</script>

<style lang="scss">
.investor-info {
  &__search {
    display: flex;
    margin-top: 15px;
  }

  &__filters {
    display: flex;
    margin-top: 15px;

    &-item {
      max-width: 250px;
      width: 100%;
      margin-right: 30px;
    }
  }
}
.investor-table {
  thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__totals {
    position: sticky;
    bottom: 0;
  }
}
.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.search-field {
  max-width: 430px !important;
}
</style>
